<template>
  <div class="alice-ai">
    <header class="alice-ai__head">
      <div class="alice-ai__head-main">
        <div class="alice-ai__crumb">
          <span>Admin</span>
          <Icon name="chevron-right" :size="12" />
          <span>AI Finetuning</span>
        </div>
        <h1 class="alice-ai__title">
          AI Finetuning
          <span class="alice-ai__count">{{ tabKeys.length }}</span>
        </h1>
        <p class="alice-ai__subtitle">
          God rights for Liza. Edit the prompts that drive Alice's analyses,
          then save. Changes apply globally. Every save keeps the previous
          version in <code>admin/finetuning/history</code>.
        </p>
        <p v-if="lastEdited" class="alice-ai__edited">{{ lastEdited }}</p>
      </div>

      <button class="alice-ai__save" :disabled="saving" @click="saveAdmin">
        <Icon name="plus" :size="16" />
        <span>{{ saving ? "Saving…" : "Save changes" }}</span>
      </button>
    </header>

    <div v-if="finetuning" class="alice-ai__body">
      <div class="alice-ai__tabs" role="tablist">
        <button
          v-for="t in tabKeys"
          :key="t"
          type="button"
          class="alice-ai__tab"
          :class="{ 'alice-ai__tab--active': tab === t }"
          role="tab"
          :aria-selected="tab === t"
          @click="tab = t"
        >
          <span>{{ t }}</span>
        </button>
        <button
          type="button"
          class="alice-ai__tab"
          :class="{ 'alice-ai__tab--active': tab === 'gemini' }"
          role="tab"
          :aria-selected="tab === 'gemini'"
          @click="tab = 'gemini'"
        >
          <span>Gemini</span>
        </button>
        <button
          type="button"
          class="alice-ai__tab"
          :class="{ 'alice-ai__tab--active': tab === 'geminiAudio' }"
          role="tab"
          :aria-selected="tab === 'geminiAudio'"
          @click="tab = 'geminiAudio'"
        >
          <span>Gemini audio</span>
        </button>
      </div>

      <div class="alice-ai__panel">
        <template v-for="t in tabKeys" :key="t">
          <div v-show="tab === t" class="alice-ai__editor">
            <div class="alice-ai__editor-meta">
              <span class="alice-ai__editor-label">Prompt</span>
              <span class="alice-ai__editor-key">{{ t }}</span>
            </div>
            <p class="alice-ai__hint">{{ hintFor(t) }}</p>
            <textarea
              v-model="finetuning[t]"
              class="alice-ai__textarea"
              rows="24"
              spellcheck="false"
            />
          </div>
        </template>

        <div v-show="tab === 'gemini'" class="alice-ai__editor">
          <div class="alice-ai__editor-meta">
            <span class="alice-ai__editor-label">Prompt</span>
            <span class="alice-ai__editor-key">gemini</span>
          </div>
          <p class="alice-ai__hint">
            Video recordings (usability sessions). Sent to Gemini with every
            4-minute chunk of an uploaded video.
          </p>
          <textarea
            v-model="gemini.prompt"
            class="alice-ai__textarea"
            rows="24"
            spellcheck="false"
          />

          <div
            v-if="user?.email === 'max@braingineers.com'"
            class="alice-ai__danger"
          >
            <div class="alice-ai__danger-text">
              <div class="alice-ai__danger-title">Clean Gemini storage</div>
              <div class="alice-ai__danger-sub">
                Deletes all files uploaded to the Gemini Files API.
                Irreversible.
              </div>
            </div>
            <button
              class="alice-ai__danger-btn"
              :disabled="cleaning"
              @click="cleanGeminiStorage"
            >
              <Icon name="trash" :size="14" />
              <span>{{ cleaning ? "Cleaning…" : "Clean storage" }}</span>
            </button>
          </div>
        </div>

        <div v-show="tab === 'geminiAudio'" class="alice-ai__editor">
          <div class="alice-ai__editor-meta">
            <span class="alice-ai__editor-label">Prompt</span>
            <span class="alice-ai__editor-key">gemini · audioPrompt</span>
          </div>
          <p class="alice-ai__hint">
            Audio-only recordings (interviews without picture). Sent to Gemini
            with every 4-minute chunk of an uploaded audio file. Moments from
            audio have no behaviour or screen fields. While this is empty, Alice
            uses the built-in default prompt.
          </p>
          <textarea
            v-model="gemini.audioPrompt"
            class="alice-ai__textarea"
            rows="24"
            spellcheck="false"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState } from "vuex";
import { doc, getDoc, setDoc, updateDoc } from "firebase/firestore";
import { db, functions } from "@/firebase";
import { httpsCallable } from "firebase/functions";

import Icon from "@/components/alice/Icon.vue";

// Bookkeeping fields written on save; never shown as prompt tabs.
const META_KEYS = ["updatedAt", "updatedBy", "updatedByName"];

// Chat fields first, then the generate-chapters templates, then anything new.
const TAB_ORDER = [
  "chatPrompt",
  "dos",
  "donts",
  "examples",
  "chatFormat",
  "prompt",
  "format",
  "system",
  "style",
];

// Where each field ends up. `prompt`/`format`/`system` are string templates
// for generateChapters: their ${…} placeholders are substituted there and
// must stay; the chat reads chatPrompt/chatFormat instead (falling back to
// prompt/format while those are empty).
const FIELD_HINTS = {
  chatPrompt:
    "Chat only — the opening block of every chat system prompt. Plain text, no ${…} placeholders (the chat does not fill them).",
  chatFormat:
    "Chat only — shown as “Insight format:” in the chat system prompt. No ${…} placeholders.",
  dos: "Chat and Generate chapters — “Do's:” in both.",
  donts: "Chat and Generate chapters — “Don'ts:” in both.",
  examples: "Chat and Generate chapters — “Examples:” in both.",
  prompt:
    "Generate chapters only (user message). That function fills ${style}, ${request.data.n}, ${format} and ${request.data.language} — leave one out and that value never reaches the model. The chat reads chatPrompt instead (this field is only its fallback while chatPrompt is empty).",
  format:
    "Generate chapters only; it fills ${request.data.suggestions}. The chat reads chatFormat instead (this field is only its fallback while chatFormat is empty).",
  system:
    "Generate chapters only (system message). It fills ${observations}, ${feedback}, ${project.main_research_question}, ${sub_research_questions}, ${project.research_context}, ${project.test_material} and ${finetuning.dos}/${finetuning.donts}/${finetuning.examples} — a placeholder that is missing means that data never reaches the model.",
  style:
    "Generate chapters only — inserted into prompt via ${style}. Not used in chat.",
};

export default {
  name: "AdminAI",
  components: { Icon },
  data() {
    return {
      tab: "",
      saving: false,
      cleaning: false,
    };
  },
  computed: {
    ...mapState({
      finetuning: (state) => state.finetuning,
      gemini: (state) => state.gemini,
      user: (state) => state.user,
    }),
    tabKeys() {
      if (!this.finetuning) return [];
      const keys = Object.keys(this.finetuning).filter(
        (k) => !META_KEYS.includes(k)
      );
      const rank = (k) => {
        const i = TAB_ORDER.indexOf(k);
        return i < 0 ? TAB_ORDER.length : i;
      };
      return keys.sort((a, b) => rank(a) - rank(b) || a.localeCompare(b));
    },
    lastEdited() {
      const f = this.finetuning;
      if (!f || !f.updatedAt) return "";
      const when = new Date(Number(f.updatedAt)).toLocaleString("nl-NL", {
        dateStyle: "medium",
        timeStyle: "short",
      });
      return `Last edited by ${
        f.updatedByName || f.updatedBy || "?"
      } on ${when}`;
    },
  },
  watch: {
    tabKeys: {
      immediate: true,
      handler(keys) {
        if (!this.tab && keys.length) this.tab = keys[0];
      },
    },
  },
  methods: {
    hintFor(key) {
      return FIELD_HINTS[key] || "Not read by any function yet.";
    },
    async saveAdmin() {
      this.saving = true;
      this.$store.commit("setLoading", true);
      try {
        const ref = doc(db, "admin", "finetuning");
        // Snapshot what is live right now (not the edited copy in the store)
        // so every save leaves a restorable version behind.
        const live = await getDoc(ref);
        const now = Date.now();
        if (live.exists()) {
          await setDoc(doc(db, "admin", "finetuning", "history", String(now)), {
            fields: live.data(),
            savedAt: now,
            savedBy: this.user?.uid || null,
            savedByName: this.user?.name || this.user?.email || null,
            note: "AdminAI save",
          });
        }
        const fields = {};
        for (const [k, v] of Object.entries(this.finetuning)) {
          if (!META_KEYS.includes(k)) fields[k] = v;
        }
        const meta = {
          updatedAt: now,
          updatedBy: this.user?.uid || null,
          updatedByName: this.user?.name || this.user?.email || null,
        };
        await updateDoc(ref, { ...fields, ...meta });
        this.$store.commit("setFinetuning", { ...this.finetuning, ...meta });
        await updateDoc(doc(db, "admin", "gemini"), this.gemini);
      } finally {
        this.saving = false;
        this.$store.commit("setLoading", false);
      }
    },
    async cleanGeminiStorage() {
      if (
        !confirm(
          "Delete all files in Gemini API storage? This cannot be undone."
        )
      ) {
        return;
      }
      this.cleaning = true;
      try {
        const fn = httpsCallable(functions, "cleanGeminiStorage");
        const res = await fn({});
        console.log(res.data);
        alert("Gemini storage cleaned.");
      } catch (e) {
        console.error(e);
        alert(`Failed: ${e.message}`);
      } finally {
        this.cleaning = false;
      }
    },
  },
};
</script>

<style scoped lang="scss">
.alice-ai {
  position: relative;
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
  overflow-y: auto;
  background: var(--vp-white);
}

/* ============== Header ============== */
.alice-ai__head {
  display: flex;
  align-items: flex-start;
  gap: 24px;
  padding: 32px 32px 24px;
  border-bottom: 1px solid var(--border-hairline);
}
.alice-ai__head-main {
  flex: 1;
  min-width: 0;
}
.alice-ai__crumb {
  display: flex;
  align-items: center;
  gap: 10px;
  font-family: var(--font-ui);
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--fg-3);
  margin-bottom: 8px;
}
.alice-ai__title {
  margin: 0;
  font-family: var(--font-display);
  font-size: 40px;
  font-weight: 700;
  letter-spacing: -0.02em;
  line-height: 1.05;
  color: var(--vp-black);
}
.alice-ai__count {
  display: inline-block;
  margin-left: 12px;
  font-family: var(--font-ui);
  font-size: 16px;
  font-weight: 700;
  color: var(--fg-3);
  vertical-align: middle;
}
.alice-ai__subtitle {
  margin: 8px 0 0;
  font-family: var(--font-body);
  font-size: 16px;
  color: var(--fg-2);
  max-width: 620px;
  line-height: 1.5;
}
.alice-ai__edited {
  margin: 6px 0 0;
  font-family: var(--font-body);
  font-size: 13px;
  color: var(--fg-3, var(--fg-2));
}
.alice-ai__save {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  height: 44px;
  padding: 0 20px;
  background: var(--vp-black);
  color: var(--vp-white);
  border: none;
  cursor: pointer;
  font-family: var(--font-ui);
  font-size: 13px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  border-radius: 0;
  flex-shrink: 0;
  transition: background 0.15s ease, color 0.15s ease;
}
.alice-ai__save:hover:not(:disabled) {
  background: var(--accent-100);
  color: var(--vp-black);
}
.alice-ai__save:disabled {
  opacity: 0.5;
  cursor: default;
}

/* ============== Body ============== */
.alice-ai__body {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
}

/* ============== Tabs ============== */
.alice-ai__tabs {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0;
  padding: 0 32px;
  height: 56px;
  flex-shrink: 0;
  border-bottom: 1px solid var(--border-hairline);
  background: var(--vp-white);
  overflow-x: auto;
}
.alice-ai__tab {
  display: inline-flex;
  align-items: center;
  height: 100%;
  padding: 0 18px;
  background: transparent;
  border: none;
  border-bottom: 2px solid transparent;
  cursor: pointer;
  font-family: var(--font-ui);
  font-size: 13px;
  font-weight: 600;
  color: var(--fg-3);
  text-transform: capitalize;
  white-space: nowrap;
  transition: color 0.12s ease, border-color 0.12s ease, background 0.12s ease;
}
.alice-ai__tab:hover {
  color: var(--vp-black);
  background: var(--bg-muted);
}
.alice-ai__tab--active,
.alice-ai__tab--active:hover {
  color: var(--vp-black);
  border-bottom-color: var(--accent-100);
  font-weight: 700;
  background: transparent;
}

/* ============== Panel / editor ============== */
.alice-ai__panel {
  padding: 24px 32px 32px;
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
}
.alice-ai__editor {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  border: 1px solid var(--border-hairline);
  background: var(--vp-white);
}
.alice-ai__editor-meta {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 10px 16px;
  border-bottom: 1px solid var(--border-hairline);
  background: var(--bg-muted);
  font-family: var(--font-ui);
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
}
.alice-ai__editor-label {
  color: var(--fg-3);
}
.alice-ai__editor-key {
  color: var(--vp-black);
}
.alice-ai__hint {
  margin: 0 0 10px;
  font-family: var(--font-body);
  font-size: 13px;
  line-height: 1.45;
  color: var(--fg-2);
  max-width: 720px;
}
.alice-ai__textarea {
  flex: 1;
  min-height: 360px;
  width: 100%;
  border: none;
  outline: none;
  resize: vertical;
  padding: 16px;
  background: var(--vp-white);
  color: var(--vp-black);
  font-family: var(--font-mono);
  font-size: 13px;
  line-height: 1.55;
  letter-spacing: 0;
}
.alice-ai__textarea:focus {
  background: #fcfcfc;
}

/* ============== Danger zone ============== */
.alice-ai__danger {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 24px;
  margin-top: 16px;
  padding: 16px 20px;
  border: 1px solid var(--vp-rood-100);
  background: var(--vp-rood-10, #fdecec);
}
.alice-ai__danger-text {
  min-width: 0;
}
.alice-ai__danger-title {
  font-family: var(--font-ui);
  font-size: 13px;
  font-weight: 700;
  letter-spacing: 0.04em;
  color: var(--vp-rood-100);
  margin-bottom: 2px;
}
.alice-ai__danger-sub {
  font-family: var(--font-body);
  font-size: 13px;
  color: var(--fg-2);
}
.alice-ai__danger-btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  height: 36px;
  padding: 0 14px;
  background: var(--vp-white);
  color: var(--vp-rood-100);
  border: 1px solid var(--vp-rood-100);
  cursor: pointer;
  font-family: var(--font-ui);
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  border-radius: 0;
  flex-shrink: 0;
  transition: background 0.12s ease, color 0.12s ease;
}
.alice-ai__danger-btn:hover:not(:disabled) {
  background: var(--vp-rood-100);
  color: var(--vp-white);
}
.alice-ai__danger-btn:disabled {
  opacity: 0.5;
  cursor: default;
}

@media (max-width: 1279px) {
  .alice-ai__head {
    padding: 24px 20px 20px;
  }
  .alice-ai__tabs {
    padding: 0 20px;
  }
  .alice-ai__panel {
    padding: 20px;
  }
  .alice-ai__danger {
    flex-direction: column;
    align-items: flex-start;
  }
}
</style>
